// The confirmation before a share (solution sharing plan, stage 7, L4; order and totals per the
// validated O20-O22 revision): the headline with totals, in a "confirm again" round what was already
// done, the users-visibility warnings, the two lists, then the statements.
import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { shareConfirmation } from "utils/sharingSwitches";
import { confirmationTotals, confirmationHeadline, notOptedInLine, resultsHeadline } from "utils/shareTable";

const names = (list) => (list || []).map((x) => x.name).join(", ");
const listBox = { maxHeight: 160, overflowY: "auto", border: "1px solid #eee", borderRadius: 1, px: 1, my: 0.5 };
const writtenElsewhere = (r) => (r.written || []).filter((w) => String(w._id) !== String(r.roleId));

export function ShareConfirmation({ targets, selected, results, confirmAgain, grantAll, serviceName }) {
  const summary = shareConfirmation(targets, selected);
  const totals = confirmationTotals(targets, selected, grantAll);
  const doneSoFar = (results || []).filter((r) => r.status === "shared" || writtenElsewhere(r).length);
  const solution = serviceName || "this solution";
  return (
    <Box sx={{ fontSize: 14 }} data-testid="share-confirmation">
      <Typography sx={{ fontSize: 15, fontWeight: 600 }} data-testid="share-headline">{confirmationHeadline(totals, solution)}</Typography>
      {notOptedInLine(totals) && <Typography sx={{ fontSize: 14, color: "#b26a00" }}>{notOptedInLine(totals)}</Typography>}
      {confirmAgain && (
        <Typography sx={{ fontSize: 14, color: "#b26a00", mt: 1 }}>
          The roles that would receive it, or who would see whose users, changed meanwhile. Please check and confirm again.
        </Typography>
      )}
      {confirmAgain && doneSoFar.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Already done in this share:</Typography>
          <Box sx={listBox}>
            {doneSoFar.map((r) => (
              <Typography key={r.roleId} sx={{ fontSize: 14 }}>
                {r.status === "shared" ? `${r.roleName || r.roleId}: shared` : `${r.roleName || r.roleId}: not shared yet`}
                {writtenElsewhere(r).length > 0 && `; its administrator roles got it: ${names(writtenElsewhere(r))}`}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {summary.usersHiddenFrom.length > 0 && (
        <Typography sx={{ fontSize: 14, mt: 1, color: "#b26a00" }}>
          {names(summary.usersHiddenFrom)} will no longer see the users of {names(summary.usersHiddenOf)}. To keep them seeing these users, cancel and add them to the share
          {grantAll ? "" : " (they need Receive switched on)"}, or an administrator can give them this solution later in the role editor.
        </Typography>
      )}
      {summary.usersShownTo.map((e) => (
        <Typography key={String(e.receiver._id)} sx={{ fontSize: 14, mt: 1, color: "#b26a00" }}>
          Users of {e.receiver.name} will be able to see the users of {names(e.roles)}.
        </Typography>
      ))}
      <Typography sx={{ fontSize: 14, mt: 1 }}>These roles will get {solution}:</Typography>
      <Box sx={listBox}>
        <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{summary.chosen.map((r) => <li key={r._id}>{r.name}</li>)}</ul>
      </Box>
      {summary.administrators.length > 0 && (
        <>
          <Typography sx={{ fontSize: 14 }}>and, as their administrator roles:</Typography>
          <Box sx={listBox}>
            <ul style={{ margin: "4px 0", paddingLeft: 18 }}>{summary.administrators.map((r) => <li key={r._id}>{r.name}</li>)}</ul>
          </Box>
        </>
      )}
      <Typography sx={{ fontSize: 14, mt: 1 }}>
        Everyone in these roles will get access to {solution} at their usual levels, never above what their administrators have on it.
      </Typography>
      <Typography sx={{ fontSize: 14, mt: 1 }}>
        Roles that can edit or delete solutions will be able to edit or delete this one, and roles that manage roles can pass it on.
      </Typography>
      <Typography sx={{ fontSize: 14, mt: 1 }}>
        The receiving roles' Role Management administrators or a grant-all admin can remove it, role by role, from every role listed here.
      </Typography>
    </Box>
  );
}

export function ShareResults({ results }) {
  const shared = (results || []).filter((r) => r.status === "shared");
  const probably = (results || []).filter((r) => r.status === "probably-shared");
  const notShared = (results || []).filter((r) => r.status !== "shared" && r.status !== "probably-shared");
  const outcome = (r) => (r.status === "shared" ? "shared" : r.status === "probably-shared" ? `probably shared (${r.reason})` : `not shared (${r.reason})`);
  return (
    <Box data-testid="share-results">
      <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>{resultsHeadline(results)}</Typography>
      <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
        {[...notShared, ...probably, ...shared].map((r) => (
          <Typography key={r.roleId} sx={{ fontSize: 14, my: 0.5 }}>
            {r.roleName || r.roleId}: {outcome(r)}
            {writtenElsewhere(r).length > 0 && `. Also given to its administrator roles: ${names(writtenElsewhere(r))}`}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
